import React, { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";

import {
  ArrowLeft,
  MapPin,
  Flag,
  Car,
  Briefcase,
  Calendar,
  Clock,
  User,
  Phone,
  Users,
  Trash2,
  Edit3,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import {
  apiRequest,
  getToken,
  SuccessToast,
  ErrorToast,
  devError,
} from "@ridecloud/shared";
import { Loader } from "../components/Loader";

const convertNumberToTimeAMPM = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${mins.toString().padStart(2, "0")} ${period}`;
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, message }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl transform transition-all">
        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
          <AlertCircle className="w-6 h-6 text-red-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
          Confirm Deletion
        </h3>
        <p className="text-gray-600 text-center mb-6">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const RoutePoint = ({ locations, type }) => (
  <div className="flex items-start gap-3 mb-4">
    <div
      className={`p-2 rounded-full ${
        type === "pickup"
          ? "bg-blue-100 text-blue-600"
          : "bg-red-100 text-red-600"
      }`}
    >
      {type === "pickup" ? (
        <MapPin className="w-4 h-4" />
      ) : (
        <Flag className="w-4 h-4" />
      )}
    </div>
    <div className="flex-1">
      <p className="text-sm font-medium text-gray-700 mb-1">
        {type === "pickup" ? "Pickup Points" : "Drop-off Points"}
      </p>
      <p className="text-gray-600 text-sm">{locations.join(" • ")}</p>
    </div>
  </div>
);

const SeatPricing = ({ seats }) => {
  const totalSeats =
    seats.front.total_seats + seats.middle.total_seats + seats.back.total_seats;
  const availableSeats =
    seats.front.available_seats +
    seats.middle.available_seats +
    seats.back.available_seats;

  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-700">
          {availableSeats} of {totalSeats} seats available
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {Object.entries(seats).map(([type, details]) => (
          <div
            key={type}
            className={`p-3 rounded-lg border ${
              details.available_seats > 0
                ? "bg-green-50 border-green-200"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <p className="text-xs font-medium text-gray-700 capitalize mb-1">
              {type}
            </p>
            <p className="text-xs text-gray-600 mb-1">
              {details.available_seats}/{details.total_seats}
            </p>
            <p
              className={`text-sm font-semibold ${
                details.available_seats > 0 ? "text-green-600" : "text-gray-500"
              }`}
            >
              ₹{details.price}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const RideCard = ({ ride, onDelete }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleDeleteClick = () => {
    setIsModalOpen(true);
  };

  const handleConfirmDelete = () => {
    onDelete(ride._id);
    setIsModalOpen(false);
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
  };

  const handleUpdateClick = () => {
    navigate(`/update-ride/${ride._id}`);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Route Details</h3>
          <div className="flex items-center gap-3">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                ride.isAvailable
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {ride.isAvailable ? "Available" : "Full"}
            </span>
            <button
              onClick={handleDeleteClick}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Route Points */}
        <RoutePoint locations={ride.pickupPoints} type="pickup" />
        <RoutePoint locations={ride.dropoffPoints} type="destination" />

        {/* Divider */}
        <div className="border-t border-gray-100 my-6"></div>

        {/* Vehicle Info */}
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-100 text-purple-600 rounded-full">
              <Car className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700 mb-1">Vehicle</p>
              <p className="text-gray-600 text-sm mb-2">{ride.vehicle.name}</p>
              <SeatPricing seats={ride.vehicle.seats} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 text-orange-600 rounded-full">
              <Briefcase className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">Luggage</p>
              <p className="text-gray-600 text-sm">
                {ride.luggage.no_of_luggage} bags (
                {ride.luggage.sizes.join(", ")})
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 text-blue-600 rounded-full">
                <Calendar className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Date</p>
                <p className="text-gray-600 text-sm">{ride.date}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 text-green-600 rounded-full">
                <Clock className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Time</p>
                <p className="text-gray-600 text-sm">
                  {convertNumberToTimeAMPM(ride.time)}
                </p>
              </div>
            </div>
          </div>

          {/* Driver Info */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Driver Information
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">
                  {ride.userId.first_name} {ride.userId.last_name}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">
                  +91 {ride.userId.phone}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Update Button */}
        <button
          onClick={handleUpdateClick}
          className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center gap-2"
        >
          <Edit3 className="w-4 h-4" />
          Update Ride Details
        </button>
      </div>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this ride?"
      />
    </div>
  );
};

const MyRides = () => {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noRidesFound, setNoRidesFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      StatusBar.setStyle({ style: Style.Dark }); // Dark icons
    }
    fetchRides();
  }, []);

  const fetchRides = async () => {
    try {
      const response = await apiRequest(async (APIv1) =>
        APIv1.get("/user/dashboard/my_rides", {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            "Content-Type": "application/json",
          },
        })
      );

      if (response.data.length === 0) {
        setNoRidesFound(true);
      } else {
        setNoRidesFound(false);
        setRides(response.data);
      }
    } catch (error) {
      ErrorToast("Failed to fetch rides");
      devError("Error fetching rides:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRide = async (rideId) => {
    try {
      await apiRequest(async (APIv1) =>
        APIv1.delete(`/user/dashboard/delete_ride/${rideId}`, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            "Content-Type": "application/json",
          },
        })
      );
      SuccessToast("Ride deleted successfully");
      fetchRides(); // Refresh the rides list after deletion
    } catch (error) {
      ErrorToast("Failed to delete ride");
      devError("Error deleting ride:", error);
    }
  };

  const handleGoBack = () => {
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <Loader text="Loading your rides..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Toaster position="top-right" />

      {/* Fixed Header - Removed container class that was causing extra padding */}
      <div
        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-4"
        style={{
          paddingTop: Capacitor.isNativePlatform()
            ? "env(safe-area-inset-top)"
            : undefined,
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center space-x-6">
            <div
              onClick={() => navigate("/dashboard")}
              className="group w-10 h-10 rounded-2xl flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-white/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer"
            >
              <ArrowLeft className="w-6 h-6 text-white group-hover:-translate-x-1 transition-transform duration-300" />
            </div>

            <div className="space-y-1">
              <h1 className="text-2xl font-bold">My Rides</h1>

              <p className="text-blue-100 text-sm">
                Manage and track all your posted rides
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {noRidesFound ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Car className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No rides posted yet
            </h3>
            <p className="text-gray-600 mb-6">
              Start by creating your first ride to connect with passengers
            </p>
            <button
              onClick={() => navigate("/post-ride")}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
            >
              Post Your First Ride
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {rides.map((ride) => (
              <RideCard
                key={ride._id}
                ride={ride}
                onDelete={handleDeleteRide}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRides;
